import { useEffect, useState } from "react";
import { Users, CalendarDays, ShoppingBag, Euro, DoorOpen, Mail, Send, Tag } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fetchPricing, formatPrice, hasPromo, promoCountdown } from "@/lib/pricing";
import { toast } from "sonner";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-amber-500/20 bg-gradient-to-b from-[#0E2A1E] to-[#081C13] p-5">
    <div className="flex items-center gap-2.5 text-amber-300/80">
      <Icon className="h-4.5 w-4.5 h-5 w-5" />
      <span className="text-xs font-semibold uppercase tracking-[0.16em]">{label}</span>
    </div>
    <p className="mt-3 font-display text-3xl font-bold text-amber-100">{value}</p>
  </div>
);

const inputCls =
  "w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-2.5 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70";

const PricingCard = () => {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchPricing().then(setPricing);
  }, []);

  if (!pricing) return null;

  const set = (key, value) => setPricing((p) => ({ ...p, [key]: value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/pricing", {
        price: parseFloat(pricing.price),
        original_price: parseFloat(pricing.original_price) || 0,
        promo_active: !!pricing.promo_active,
        promo_label: pricing.promo_label || "",
        promo_ends_at: pricing.promo_ends_at || null,
      });
      toast.success("Prezzo aggiornato: è già visibile sul sito.");
    } catch (err) {
      toast.error(formatApiError(err, "Salvataggio non riuscito."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/50 p-6" data-testid="pricing-card">
      <div className="flex items-center gap-2.5">
        <Tag className="h-4 w-4 text-amber-300" />
        <h3 className="font-display text-lg font-bold text-amber-100">Prezzo del calendario</h3>
      </div>
      <p className="mt-1.5 text-xs text-emerald-100/60">
        Il prezzo si aggiorna subito ovunque: home, caselle bloccate e dashboard.
      </p>
      <form onSubmit={save} className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="pricing-form">
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-emerald-100/60">Prezzo di vendita (€)</span>
          <input
            type="number"
            min="0.5"
            step="0.01"
            required
            data-testid="pricing-price-input"
            value={pricing.price}
            onChange={(e) => set("price", e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-emerald-100/60">Prezzo barrato (€, opzionale)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            data-testid="pricing-original-input"
            value={pricing.original_price}
            onChange={(e) => set("original_price", e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="flex items-center gap-2.5 text-sm text-emerald-100/80" data-testid="pricing-promo-label-wrap">
          <input
            type="checkbox"
            data-testid="pricing-promo-checkbox"
            checked={!!pricing.promo_active}
            onChange={(e) => set("promo_active", e.target.checked)}
            className="h-4 w-4 accent-amber-500"
          />
          Mostra il prezzo barrato e l'etichetta promo
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-emerald-100/60">Etichetta promo</span>
          <input
            type="text"
            maxLength={40}
            data-testid="pricing-label-input"
            value={pricing.promo_label}
            onChange={(e) => set("promo_label", e.target.value)}
            placeholder="Es. Offerta lancio"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-emerald-100/60">Fine promo</span>
          <input
            type="datetime-local"
            data-testid="pricing-ends-input"
            value={toLocalInput(pricing.promo_ends_at)}
            onChange={(e) => set("promo_ends_at", e.target.value)}
            className={inputCls}
          />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2">
          <p className="text-sm text-emerald-100/70" data-testid="pricing-preview">
            Anteprima:{" "}
            {hasPromo(pricing) && (
              <span className="mr-1.5 line-through opacity-60">{formatPrice(pricing.original_price)}</span>
            )}
            <span className="font-display text-lg font-bold gold-text">{formatPrice(pricing.price)}</span>
            {hasPromo(pricing) && pricing.promo_label && (
              <span className="ml-2 rounded-full bg-[#8B1E2B] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-100">
                {pricing.promo_label}
              </span>
            )}
            {promoCountdown(pricing) && (
              <span className="ml-2 text-xs text-amber-200/80" data-testid="pricing-countdown">
                · scade {promoCountdown(pricing)}
              </span>
            )}
          </p>
          <button
            type="submit"
            data-testid="pricing-save-button"
            disabled={busy}
            className="rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 px-5 py-2.5 text-sm font-bold text-[#1A1204] transition-transform hover:scale-[1.02] disabled:opacity-60"
          >
            {busy ? "Salvataggio..." : "Salva prezzo"}
          </button>
        </div>
      </form>
    </div>
  );
};

export const OverviewTab = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!stats) {
    return <div className="h-10 w-10 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" data-testid="stats-loading" />;
  }

  return (
    <div className="space-y-8" data-testid="overview-tab">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Users} label="Utenti" value={stats.users} testid="stat-users" />
        <StatCard icon={ShoppingBag} label="Ordini" value={stats.orders} testid="stat-orders" />
        <StatCard icon={Euro} label="Ricavi" value={`€ ${stats.revenue.toFixed(2).replace(".", ",")}`} testid="stat-revenue" />
        <StatCard icon={CalendarDays} label="Calendari attivi" value={stats.calendars} testid="stat-calendars" />
        <StatCard icon={DoorOpen} label="Caselle aperte" value={stats.opened_doors} testid="stat-opened" />
        <StatCard icon={Mail} label="Iscritti newsletter" value={stats.subscribers} testid="stat-subscribers" />
        <StatCard icon={Send} label="Campagne inviate" value={stats.campaigns_sent} testid="stat-campaigns" />
      </div>

      <PricingCard />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/50 p-6">
          <h3 className="font-display text-lg font-bold text-amber-100">Ultimi iscritti</h3>
          <ul className="mt-4 space-y-3" data-testid="recent-users-list">
            {stats.recent_users.length === 0 && <li className="text-sm text-emerald-100/50">Nessun utente ancora.</li>}
            {stats.recent_users.map((u) => (
              <li key={u.id} className="flex items-center justify-between text-sm">
                <span className="text-emerald-100/85">{u.name} <span className="text-emerald-100/45">· {u.email}</span></span>
                <span className="text-xs text-emerald-100/45">{new Date(u.created_at).toLocaleDateString("it-IT")}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/50 p-6">
          <h3 className="font-display text-lg font-bold text-amber-100">Ultimi ordini</h3>
          <ul className="mt-4 space-y-3" data-testid="recent-orders-list">
            {stats.recent_orders.length === 0 && <li className="text-sm text-emerald-100/50">Nessun ordine ancora.</li>}
            {stats.recent_orders.map((o) => (
              <li key={o.id} className="flex items-center justify-between text-sm">
                <span className="font-mono text-amber-100/90">{o.code}</span>
                <span className="text-xs text-emerald-100/45">
                  {o.kind === "gift" ? "Regalo" : "Personale"} · {new Date(o.created_at).toLocaleDateString("it-IT")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
